/**
 * Layout shell for the public onboarding pages: a calm branded canvas with a
 * single floating card, the brand lockup on top and a light footer. Matches the
 * panel's Classic look (grey canvas + soft floating card) so the funnel reads as
 * the same product as the panel the client lands in afterwards.
 */

import React from 'react';
import { Box, Paper, Typography, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import BrandLockup from '@/components/common/BrandLockup';
import { APP_NAME } from '@/auth/authConfig';

const Canvas = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(3),
  gap: theme.spacing(3),
  background:
    (theme as any).custom?.login?.containerGradient?.(theme.palette) ||
    `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[200]} 100%)`,
}));

interface PublicShellProps {
  children: React.ReactNode;
  /** Card max width in px (default 480, wider for the plan grid). */
  maxWidth?: number;
}

export const PublicShell: React.FC<PublicShellProps> = ({ children, maxWidth = 480 }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Canvas>
      <Box sx={{ mt: { xs: 3, sm: 6 } }}>
        <BrandLockup orientation="horizontal" size={isMobile ? 40 : 46} />
      </Box>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth,
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow:
            '0 1px 2px rgba(20,40,60,.04), 0 4px 16px rgba(20,40,60,.05)',
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {children}
      </Paper>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 3 }}>
        © {new Date().getFullYear()} {APP_NAME}
      </Typography>
    </Canvas>
  );
};

export default PublicShell;
